import React, { CSSProperties, ReactNode } from "react"
import { useField } from "formik"
import { NumericFormat, type NumberFormatValues } from "react-number-format"
import { FormHelperText, Grid, InputAdornment, TextField } from "@mui/material"

import { useNumber } from "../../../../localization/useNumber"

import {
    monetaryRangeAllowsNegative,
    monetaryRangeIsNonPositive,
    type MonetaryRange,
} from "../../../../../shared/components/forms/numericFieldTypes"

type FormikCurrencyFieldProps = Readonly<{
    name: string
    // Sign constraint applied at input time; validation of the exact bound stays in the form's schema.
    range?: MonetaryRange
    // Currency code shown as a leading decorator when no explicit startAdornment is given.
    currency?: string
    // Optional: the product form renders its own label above the input, so the field has none of its own
    label?: string
    // Id of that external label, wired up as `aria-labelledby` so the input keeps an accessible name
    labelId?: string
    placeholder?: string | undefined
    errorMessage?: string
    fullWidth?: boolean
    inputTextAlign?: CSSProperties["textAlign"]
    containerDirection?: CSSProperties["flexDirection"]
    gap?: number
    startAdornment?: ReactNode
    disabled?: boolean
    isAllowed?: (values: NumberFormatValues) => boolean
    className?: string
}>

export const FormikCurrencyField = ({
    name,
    range,
    currency,
    label,
    labelId,
    placeholder,
    errorMessage,
    fullWidth = true,
    inputTextAlign = "right",
    containerDirection = "row",
    gap = 1,
    startAdornment,
    disabled,
    isAllowed,
    className,
}: FormikCurrencyFieldProps) => {
    const [field, _, helpers] = useField<string>(name)

    const allowNegative = range !== undefined ? monetaryRangeAllowsNegative(range) : true
    const combinedIsAllowed =
        range !== undefined && monetaryRangeIsNonPositive(range)
            ? (values: NumberFormatValues) =>
                  (isAllowed?.(values) ?? true) && (values.floatValue === undefined || values.floatValue <= 0)
            : isAllowed

    const resolvedStartAdornment =
        startAdornment ?? (currency ? <InputAdornment position="start">{currency}</InputAdornment> : undefined)

    return (
        <Grid container gap={gap} flexDirection={containerDirection}>
            <TextField
                className={className}
                name={name}
                label={label}
                placeholder={placeholder}
                value={field.value}
                fullWidth={fullWidth}
                disabled={disabled}
                inputProps={{
                    style: { textAlign: inputTextAlign },
                    isAllowed: combinedIsAllowed,
                    allowNegative,
                    "aria-labelledby": labelId,
                }}
                onChange={(e) => {
                    helpers.setValue(e.target.value)
                }}
                InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    startAdornment: resolvedStartAdornment,
                }}
                error={!!errorMessage}
            />
            {!!errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Grid>
    )
}

interface NumberFormatCustomProps {
    inputRef: (instance: HTMLInputElement | null) => void
    onChange: React.FormEventHandler<HTMLInputElement>
    name: string
    isAllowed?: (values: NumberFormatValues) => boolean
    allowNegative?: boolean
}

export const NumberFormatCustom: React.FC<NumberFormatCustomProps> = (props) => {
    const { inputRef, onChange, name, ...other } = props
    const numberFactory = useNumber({})

    return (
        <NumericFormat
            {...other}
            name={name}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                } as React.ChangeEvent<HTMLInputElement>)
            }}
            thousandSeparator={numberFactory.groupSeparator}
            decimalSeparator={numberFactory.decimalSeparator}
            decimalScale={2}
            prefix=""
        />
    )
}
